import React, { useContext, useEffect, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  Radio
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import ClearIcon from '@mui/icons-material/Clear';
import AuthContext from '../../contexts/AuthContext';

const ReviewAndSubmitStep = ({
  npiData,
  practiceNpiData,
  editedDetails,
  practiceDetails,
  errors,
  loading,
  onDetailChange,
  onPracticeDetailChange,
  onBack,
  onSubmit,
  onOpenClearProviderDialog,
  onOpenClearPracticeDialog,
  doingInHouse,
  inHouseAgreed,
  onChangeDoingInHouse,
  onChangeInHouseAgreed,
}) => {
  const { tokens } = useContext(AuthContext);
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
  const [ifuDialogOpen, setIfuDialogOpen] = useState(false);
  const [ifuUrl, setIfuUrl] = useState(null);
  const [ifuLoading, setIfuLoading] = useState(false);
  const [ifuError, setIfuError] = useState('');
  const [hasViewedOnce, setHasViewedOnce] = useState(Boolean(inHouseAgreed));

  useEffect(() => () => {
    if (ifuUrl) {
      try { window.URL.revokeObjectURL(ifuUrl); } catch {}
    }
  }, [ifuUrl]);

  const openIfu = async () => {
    setIfuError('');
    setIfuLoading(true);
    setIfuDialogOpen(true);
    setHasViewedOnce(true);
    try {
      const res = await fetch(`${apiBase}/agreements/key/ifu/document`, {
        headers: { Authorization: `Bearer ${tokens.id_token}` },
      });
      if (!res.ok) throw new Error('Failed to load IFU');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      setIfuUrl(url);
    } catch (e) {
      setIfuError(e.message || 'Failed to load IFU');
    } finally {
      setIfuLoading(false);
    }
  };
  const closeIfu = () => setIfuDialogOpen(false);
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!npiData || !practiceNpiData) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Alert severity="error">
          Failed to load NPI data. Please try again.
        </Alert>
      </Box>
    );
  }
  return (
    <Box sx={{ maxWidth: 600 }}>
      <Typography variant="h6" gutterBottom>
        Review and submit
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        Please review and update all the information below before submitting.
      </Typography>
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
          Provider information
        </Typography>
        <Box sx={{ pl: 2, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              label="NPI Number"
              value={npiData.number}
              disabled
            />
            <IconButton onClick={onOpenClearProviderDialog}>
              <ClearIcon />
            </IconButton>
          </Box>
          {['name_prefix','first_name','middle_name','last_name','name_suffix','credential'].map(field => (
            <TextField
              key={field}
              fullWidth
              label={(function(){ const b = field.replace(/_/g,' ').trim(); return b ? b.replace(/\b\w/g, c => c.toUpperCase()) : ''; })()}
              value={editedDetails[field] || ''}
              onChange={onDetailChange(field)}
              error={!!errors[field]}
              helperText={errors[field]}
              sx={{ mb: 2 }}
            />
          ))}
          <TextField
            fullWidth
            label="Phone Number"
            value={editedDetails.provider_phone || ''}
            onChange={onDetailChange('provider_phone')}
            error={!!errors.provider_phone}
            helperText={errors.provider_phone}
            required
            data-error={!!errors.provider_phone}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Fax Number"
            value={editedDetails.provider_fax || ''}
            onChange={onDetailChange('provider_fax')}
            error={!!errors.provider_fax}
            helperText={errors.provider_fax}
            required
            data-error={!!errors.provider_fax}
            sx={{ mb: 2 }}
          />
        </Box>
        {/* On-site collections */}
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
          On-site collections
        </Typography>
        <Box sx={{ pl: 2, mb: 4 }}>
          <RadioGroup
            row
            value={doingInHouse ? 'yes' : 'no'}
            onChange={(e) => {
              const yes = e.target.value === 'yes';
              onChangeDoingInHouse?.(yes);
              if (!yes) {
                onChangeInHouseAgreed?.(false);
              }
            }}
          >
            <FormControlLabel value="yes" control={<Radio />} label="Yes" />
            <FormControlLabel value="no" control={<Radio />} label="No" />
          </RadioGroup>
          {doingInHouse && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
              <Button variant="outlined" onClick={openIfu}>View IFU</Button>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={inHouseAgreed || false}
                    onChange={(e) => onChangeInHouseAgreed?.(e.target.checked)}
                    disabled={!hasViewedOnce}
                  />
                }
                label="I have reviewed the IFU and agree"
              />
              {!hasViewedOnce && (
                <Typography variant="caption" color="text.secondary">
                  Click "View IFU" to enable agreement.
                </Typography>
              )}
            </Box>
          )}
        </Box>

        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
          Practice information
        </Typography>
        <Box sx={{ pl: 2, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              label="Practice NPI Number"
              value={practiceNpiData.number}
              disabled
            />
            <IconButton onClick={onOpenClearPracticeDialog}>
              <ClearIcon />
            </IconButton>
          </Box>
          <TextField
            fullWidth
            label="Practice Name (Legal Name)"
            value={practiceDetails.practice_name}
            onChange={onPracticeDetailChange('practice_name')}
            error={!!errors.practice_name}
            helperText={errors.practice_name}
            required
            data-error={!!errors.practice_name}
            sx={{ mb: 2 }}
          />
          {/* Corporate Address Section */}
          <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'left' }}>
            Corporate address
          </Typography>
          <TextField
            fullWidth
            label="Street Address"
            value={practiceDetails.corporate_street_1}
            onChange={onPracticeDetailChange('corporate_street_1')}
            error={!!errors.corporate_street_1}
            helperText={errors.corporate_street_1}
            required
            data-error={!!errors.corporate_street_1}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
              label="Address Line 2"
            value={practiceDetails.corporate_street_2}
            onChange={onPracticeDetailChange('corporate_street_2')}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="City"
              value={practiceDetails.corporate_city}
              onChange={onPracticeDetailChange('corporate_city')}
              error={!!errors.corporate_city}
              helperText={errors.corporate_city}
              required
              data-error={!!errors.corporate_city}
            />
            <TextField
              fullWidth
              label="State"
              value={practiceDetails.corporate_state}
              onChange={onPracticeDetailChange('corporate_state')}
              error={!!errors.corporate_state}
              helperText={errors.corporate_state}
              required
              data-error={!!errors.corporate_state}
            />
            <TextField
              fullWidth
              label="ZIP Code"
              value={practiceDetails.corporate_zip_code}
              onChange={onPracticeDetailChange('corporate_zip_code')}
              error={!!errors.corporate_zip_code}
              helperText={errors.corporate_zip_code}
              required
              data-error={!!errors.corporate_zip_code}
            />
          </Box>
          {/* End Corporate Address Section */}
          <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'left' }}>
            Billing address
          </Typography>
          <TextField
            fullWidth
            label="Street Address"
            value={practiceDetails.billing_street_1}
            onChange={onPracticeDetailChange('billing_street_1')}
            error={!!errors.billing_street_1}
            helperText={errors.billing_street_1}
            required
            data-error={!!errors.billing_street_1}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
              label="Address Line 2"
            value={practiceDetails.billing_street_2}
            onChange={onPracticeDetailChange('billing_street_2')}
            error={!!errors.billing_street_2}
            helperText={errors.billing_street_2}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="City"
              value={practiceDetails.billing_city}
              onChange={onPracticeDetailChange('billing_city')}
              error={!!errors.billing_city}
              helperText={errors.billing_city}
              required
              data-error={!!errors.billing_city}
            />
            <TextField
              fullWidth
              label="State"
              value={practiceDetails.billing_state}
              onChange={onPracticeDetailChange('billing_state')}
              error={!!errors.billing_state}
              helperText={errors.billing_state}
              required
              data-error={!!errors.billing_state}
            />
            <TextField
              fullWidth
              label="ZIP Code"
              value={practiceDetails.billing_zip_code}
              onChange={onPracticeDetailChange('billing_zip_code')}
              error={!!errors.billing_zip_code}
              helperText={errors.billing_zip_code}
              required
              data-error={!!errors.billing_zip_code}
            />
          </Box>
          <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'left' }}>
            Shipping address
          </Typography>
          <TextField
            fullWidth
            label="Street Address"
            value={practiceDetails.shipping_street_1}
            onChange={onPracticeDetailChange('shipping_street_1')}
            error={!!errors.shipping_street_1}
            helperText={errors.shipping_street_1}
            required
            data-error={!!errors.shipping_street_1}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
              label="Address Line 2"
            value={practiceDetails.shipping_street_2}
            onChange={onPracticeDetailChange('shipping_street_2')}
            error={!!errors.shipping_street_2}
            helperText={errors.shipping_street_2}
            sx={{ mb: 2 }}
          />
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth
              label="City"
              value={practiceDetails.shipping_city}
              onChange={onPracticeDetailChange('shipping_city')}
              error={!!errors.shipping_city}
              helperText={errors.shipping_city}
              required
              data-error={!!errors.shipping_city}
            />
            <TextField
              fullWidth
              label="State"
              value={practiceDetails.shipping_state}
              onChange={onPracticeDetailChange('shipping_state')}
              error={!!errors.shipping_state}
              helperText={errors.shipping_state}
              required
              data-error={!!errors.shipping_state}
            />
            <TextField
              fullWidth
              label="ZIP Code"
              value={practiceDetails.shipping_zip_code}
              onChange={onPracticeDetailChange('shipping_zip_code')}
              error={!!errors.shipping_zip_code}
              helperText={errors.shipping_zip_code}
              required
              data-error={!!errors.shipping_zip_code}
            />
          </Box>
          <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'left' }}>
            Practice information
          </Typography>
          <TextField
            fullWidth
            label="DBA"
            value={practiceDetails.practice_dba}
            onChange={onPracticeDetailChange('practice_dba')}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="EIN"
            value={practiceDetails.practice_ein}
            onChange={onPracticeDetailChange('practice_ein')}
            error={!!errors.practice_ein}
            helperText={errors.practice_ein}
            required
            data-error={!!errors.practice_ein}
            sx={{ mb: 2 }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={practiceDetails.practice_request_tax_exemption}
                onChange={(e) => onPracticeDetailChange('practice_request_tax_exemption')(e)}
              />
            }
            label="Request Tax Exemption"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="State tax ID"
            value={practiceDetails.practice_state_tax_id}
            onChange={onPracticeDetailChange('practice_state_tax_id')}
            error={practiceDetails.practice_request_tax_exemption && !!errors.practice_state_tax_id}
            helperText={practiceDetails.practice_request_tax_exemption ? errors.practice_state_tax_id : ''}
            required={practiceDetails.practice_request_tax_exemption}
            data-error={practiceDetails.practice_request_tax_exemption && !!errors.practice_state_tax_id}
            sx={{ mb: 2 }}
          />
          <Autocomplete
            freeSolo
            options={practiceNpiData.taxonomies?.map(t => t.desc) || []}
            value={practiceDetails.practice_type}
            onChange={(e, newValue) =>
              onPracticeDetailChange('practice_type')({ target: { value: newValue } })
            }
            renderInput={(params) => (
              <TextField {...params} label="Practice Type" fullWidth sx={{ mb: 2 }} />
            )}
          />
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
            Contact information
          </Typography>
          <TextField
            fullWidth
            label="Phone Number"
            value={practiceDetails.practice_phone}
            onChange={onPracticeDetailChange('practice_phone')}
            error={!!errors.practice_phone}
            helperText={errors.practice_phone}
            required
            data-error={!!errors.practice_phone}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Fax Number"
            value={practiceDetails.practice_fax}
            onChange={onPracticeDetailChange('practice_fax')}
            error={!!errors.practice_fax}
            helperText={errors.practice_fax}
            required
            data-error={!!errors.practice_fax}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Email"
            value={practiceDetails.practice_email}
            onChange={onPracticeDetailChange('practice_email')}
            error={!!errors.practice_email}
            helperText={errors.practice_email}
            required
            data-error={!!errors.practice_email}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Website"
            value={practiceDetails.practice_website}
            onChange={onPracticeDetailChange('practice_website')}
            error={!!errors.practice_website}
            helperText={errors.practice_website}
            sx={{ mb: 2 }}
          />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
        <Button variant="outlined" onClick={onBack}>
          Back
        </Button>
        <Button variant="contained" onClick={onSubmit} disabled={doingInHouse && !inHouseAgreed}>
          Submit for Review
        </Button>
      </Box>
      <Dialog open={ifuDialogOpen} onClose={closeIfu} maxWidth="md" fullWidth>
        <DialogTitle>Instructions for Use (IFU)</DialogTitle>
        <DialogContent dividers sx={{ minHeight: 400 }}>
          {ifuError && <Alert severity="error" sx={{ mb: 2 }}>{ifuError}</Alert>}
          {ifuLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress />
            </Box>
          )}
          {!ifuLoading && ifuUrl && (
            <iframe title="ifu" src={ifuUrl} style={{ width: '100%', height: '70vh', border: 'none' }} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeIfu}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReviewAndSubmitStep;
 
